<template>
  <PageWrapper>
    <div class="container">
      <ProductsHeader :category="category ?? null" />
      <div class="flex c-justify-end c-items-center">
        <strong class="c-mr-2">{{ t('sort_by') }}</strong>
        <select
          id="sort-type"
          v-model="selectedSort"
          class="custom-select text-left c-rounded-sm w-auto"
          @change="sortProducts"
        >
          <option
            v-for="(sort, index) in sortTypes"
            :key="index"
            :value="sort.name"
          >
            {{ sort.label }}
          </option>
        </select>
      </div>
      <ProductsCatalogGrid
        :is-loaded="isLoaded"
        :products="filteredProducts"
      />
      <div
        v-if="isLoaded && filteredProducts.length === 0"
        class="text-center"
      >
        {{ t('no_product_meets_selected_filtration') }}
      </div>
      <BaseCategoriesSection
        v-if="isLoaded"
        :title="t('categories_title')"
      />
    </div>
  </PageWrapper>
  <BaseModal modal-id="product-detail-modal">
    <ModalProductDetail />
  </BaseModal>
  <BaseModal
    :title="t('watchdog_title')"
    modal-id="modal-watchdog"
  >
    <ModalWatchdog />
  </BaseModal>
</template>

<script setup>
import { useRouteQuery } from '@vueuse/router'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import useHead from '@/mixins/headManager'

import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import PageWrapper from '@/components/general/BasePageWrapper.vue'
import ProductsCatalogGrid from '@/components/products/ProductsCatalogGrid.vue'
import ProductsHeader from '@/components/products/ProductsHeader.vue'
import { GtmEventEmitter } from '@/utils/gtmEventEmitter'
import BaseCategoriesSection from "@/components/general/BaseCategoriesSection.vue";
import { removeValueFromObject } from '@/mixins/utils'
import { useAppStore } from '@/stores/AppStore'
import { useNavbarStore } from '@/stores/NavbarStore'
import { useProductStore } from '@/stores/ProductStore'

const BaseModal = defineAsyncComponent(() => import('@/components/general/BaseModal.vue'))
const ModalProductDetail = defineAsyncComponent(() => import('@/components/modals/ModalProductDetail.vue'))
const ModalWatchdog = defineAsyncComponent(() => import('@/components/modals/ModalWatchdog.vue'))

const { t } = useI18n()

const sortTypes = [
  {
    label: t('best_selling'),
    name: 'bestsellers'
  },
  {
    label: t('from_cheapest'),
    name: 'price_asc'
  },
  {
    label: t('from_most_expensive'),
    name: 'price_desc'
  },
  {
    label: t('best_rating'),
    name: 'rating_desc'
  },
];

const route = useRoute()
const isLoaded = ref(false)
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const url = (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.path

const products = ref([])
const category = ref()
const kind = ref()
const categoryTitle = ref()
const selectedSort = ref('bestsellers')

// Inits navbars
const { setNavbar } = useNavbarStore()

setNavbar({
  backgroundColor: 'bg-primary-700',
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: url,
    },
  ],
})

const fetchData = async () => {
  isLoaded.value = false
  const { data } = await ApiServiceV1.get(`products?permalink=${route.params.permalink}`)
  category.value = data.category
  products.value = data.products
  kind.value = data.category.systemName
  categoryTitle.value = data.category.title
  isLoaded.value = true
  route.meta.pageReady = true
  GtmEventEmitter.viewItemList({
    category: data.category,
    products: data.products,
  });
  
  

  useHead({
    title: category.value.ogTitle || category.value.title,
    meta: [
      {
        property: `og:title`,
        content: category.value.ogTitle || category.value.title,
      },
      {
        property: `og:description`,
        content: category.value.ogDescription || category.value.description,
      },
    ],
  });
}

const sortProducts = (inputProducts = []) => {
  if (!Array.isArray(inputProducts)) return []

  const tempProducts = [...inputProducts]

  switch(selectedSort.value){
    case 'price_asc': return tempProducts.sort((a, b) => a.price - b.price)
    case 'price_desc': return tempProducts.sort((a, b) => b.price - a.price)
    case 'rating_desc': return tempProducts.sort((a, b) => b.starRating - a.starRating)
    default: return tempProducts
  }
}

const productStore = useProductStore()
const { filterCount, isFavoritesInFilter, getFilters, filters } = storeToRefs(productStore)
const appStore = useAppStore()

const { getFavoriteProducts } = storeToRefs(appStore)

const favoritesProducts = computed(() =>
  products.value.filter((product) => getFavoriteProducts.value.includes(product.id)),
)
const cleanProducts = computed(() => (isFavoritesInFilter.value ? favoritesProducts.value : products.value))
const cleanFilters = computed(() => removeValueFromObject(getFilters.value, 'favorites')) // "favorites" needs to be removed from filters and handled little differently

const filteredProducts = computed(() => {
  return sortProducts(cleanProducts.value.filter((product) =>
    cleanFilters.value.every((f) => product.properties.some((property) => property.systemName === f)),
  ))
})

const urlFilter = useRouteQuery('filter')
if (urlFilter.value) {
  productStore.setFilter(urlFilter.value.split(' '))
}

watch(
  () => filterCount.value,
  () => {
    urlFilter.value = filters.value.join(' ')
  },
)

watch(
  () => route.params.permalink,

  async () => {
    productStore.clearFilters()
    fetchData()
  },
  { immediate: true },
)
</script>

<i18n>
  {
    "cs": {
    "best_rating": "Nejlépe hodnocené",
    "best_selling": "Nejprodávanější",
    "categories_title": "Nevybrali jste si? Máme toho více!",
    "from_cheapest": "Nejlevnější",
    "from_most_expensive": "Nejdražší",
    "no_product_meets_selected_filtration": "Žádný produkt nevyhovuje zvolené filtraci",
    "sort_by": "Řazení",
    "watchdog_title": "Chcete si počkat?"
},
    "sk": {
    "best_rating": "Najlepšie hodnotené",
    "best_selling": "Najpredávanejšie",
    "categories_title": "Nevybrali ste si? Máme toho viac!",
    "from_cheapest": "Najlacnejšie",
    "from_most_expensive": "Najdrahšie",
    "no_product_meets_selected_filtration": "Žiadny produkt nevyhovuje zvolenej filtrácii",
    "sort_by": "Zoradiť podľa",
    "watchdog_title": "Chcete si počkať?"
},
    "pl": {
    "best_rating": "Najwyżej oceniane",
    "best_selling": "Bestsellery",
    "categories_title": "Nie wiesz co wybrać? Zobacz więcej!",
    "from_cheapest": "Najtańsze",
    "from_most_expensive": "Najdroższe",
    "no_product_meets_selected_filtration": "Żaden z produktów nie odpowiada zaznaczonym właściwościom",
    "sort_by": "Sortuj według",
    "watchdog_title": "Chcesz poczekać?"
},
    "hu": {
    "best_rating": "Legjobban értékelt",
    "best_selling": "Legnépszerűbbek",
    "categories_title": "Nem választottál? Van még több is!",
    "from_cheapest": "Legolcsóbb",
    "from_most_expensive": "Legdrágább",
    "no_product_meets_selected_filtration": "A szűrő beállításainak egyetlen termék sem felel meg",
    "sort_by": "Rendezés",
    "watchdog_title": "Szeretnél várni?"
},
    "de": {
    "best_rating": "Am besten bewertet",
    "best_selling": "Bestseller",
    "categories_title": "Noch nicht das Richtige gefunden? Wir haben mehr! ",
    "from_cheapest": "Günstigste",
    "from_most_expensive": "Teuerste",
    "no_product_meets_selected_filtration": "Kein Produkt entspricht dem ausgewählten Filter",
    "sort_by": "Sortieren nach",
    "watchdog_title": "Möchtest du warten?"
},
    "at": {
    "best_rating": "Am besten bewertet",
    "best_selling": "Bestseller",
    "categories_title": "Noch nicht das Richtige gefunden? Wir haben mehr! ",
    "from_cheapest": "Günstigste",
    "from_most_expensive": "Teuerste",
    "no_product_meets_selected_filtration": "Kein Produkt entspricht dem ausgewählten Filter",
    "sort_by": "Sortieren nach",
    "watchdog_title": "Möchtest du warten?"
},
    "ro": {
    "best_rating": "Cele mai bine cotate",
    "best_selling": "Cele mai bine vândute",
    "categories_title": "Nu v-ați hotărât? Avem mai multe!",
    "from_cheapest": "Cele mai ieftine",
    "from_most_expensive": "Cele mai scumpe",
    "no_product_meets_selected_filtration": "Niciun produs nu se potrivește cu filtrul selectat",
    "sort_by": "Sortare",
    "watchdog_title": "Dorești să aștepți?"
},
    "fr": {
    "best_rating": "Vos produits préférés",
    "best_selling": "Meilleures ventes",
    "categories_title": "Vous n’avez pas trouvé votre bonheur ? On en a encore plein d’autres !",
    "from_cheapest": "Le moins cher",
    "from_most_expensive": "Le plus cher",
    "no_product_meets_selected_filtration": "Aucun produit ne correspond aux filtres sélectionnés",
    "sort_by": "Trier par",
    "watchdog_title": "Voulez-vous patienter ?"
},
    "it": {
    "best_rating": "I più apprezzati",
    "best_selling": "I più venduti",
    "categories_title": "Non hai trovato quello che cercavi? Offriamo molto di più!",
    "from_cheapest": "I più economici",
    "from_most_expensive": "I più costosi",
    "no_product_meets_selected_filtration": "Nessun prodotto corrisponde al filtro selezionato.",
    "sort_by": "Ordina per",
    "watchdog_title": "Vuoi aspettare?"
}
  }
</i18n>
